import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import { OnboardingProgress } from './GrowthFeatures';
import { RecentlyViewed } from './UserFeatures';
import HomeCoverageHeatmap from './HomeCoverageHeatmap';
import AskYourStack from './AskYourStack';
import SkeletonBlock from './common/SkeletonBlock';
import NarrativeKPI from './common/NarrativeKPI';
import GlobalFilterBar, { GlobalFiltersProvider } from './common/GlobalFilterBar';
import EmptyState from './common/EmptyState';
import { detectPersonaDefaults } from '../lib/uiHelpers';
import { useTranslation } from '../i18n';
import {
  Building2, Package, Server, Cable, Shield, TrendingUp, BarChart3, Layers, AlertTriangle, Users, Settings, X, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomeDashboard({ token, role, permissions, isMobile, onNavigate, onJumpProperty }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const persona = useMemo(() => detectPersonaDefaults(role, permissions), [role, permissions]);

  const fetchWidgets = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/user/dashboard-widgets`, { headers });
      setWidgetConfig(res.data);
    } catch { /* use defaults */ }
  }, [headers]);

  const saveWidgets = async (config) => {
    setWidgetConfig(config);
    try {
      await axios.put(`${BACKEND_URL}/api/user/dashboard-widgets`, { widgets: config }, { headers });
    } catch { /* best effort */ }
  };

  const isWidgetVisible = (id) => {
    if (!widgetConfig) return true;
    const w = widgetConfig.find(w => w.id === id);
    return w ? w.visible !== false : true;
  };

  const toggleWidget = (id) => {
    const config = widgetConfig || [];
    const existing = config.find(w => w.id === id);
    let newConfig;
    if (existing) {
      newConfig = config.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
    } else {
      newConfig = [...config, { id, visible: false, order: config.length }];
    }
    saveWidgets(newConfig);
  };

  useEffect(() => {
    fetchWidgets();
    const fetchAll = async () => {
      try {
        const [propsRes, catsRes, platsRes, vendorsRes, productsRes, brandsRes, coverageRes, adoptionRes, vendorConcRes, tenantRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/properties`, { headers }),
          axios.get(`${BACKEND_URL}/api/categories`, { headers }),
          axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
          axios.get(`${BACKEND_URL}/api/vendors?limit=1`, { headers }),
          axios.get(`${BACKEND_URL}/api/products?limit=1`, { headers }),
          axios.get(`${BACKEND_URL}/api/brands`, { headers }),
          axios.get(`${BACKEND_URL}/api/reports/coverage-matrix`, { headers }),
          axios.get(`${BACKEND_URL}/api/reports/solution-adoption`, { headers }),
          axios.get(`${BACKEND_URL}/api/reports/vendor-concentration`, { headers }),
          axios.get(`${BACKEND_URL}/api/current-tenant`, { headers }).catch(() => ({ data: {} })),
        ]);

        const properties = propsRes.data;
        setProperties(properties);
        const propertyIds = new Set(properties.map(p => p.id));
        const coverage = coverageRes.data;
        const adoption = adoptionRes.data;
        const vendorConc = vendorConcRes.data;

        // Filter coverage to only tenant properties
        const tenantCoverage = (coverage.properties || []).filter(p => propertyIds.has(p.property_id));

        // Avg solutions per property
        const avgSolutions = tenantCoverage.length > 0
          ? Math.round(tenantCoverage.reduce((sum, p) => sum + p.total_used, 0) / tenantCoverage.length)
          : 0;

        // Property with most/least solutions
        const sorted = [...tenantCoverage].map(p => ({
          property_id: p.property_id,
          property_name: p.property_name,
          total_used: p.total_used || 0,
        })).sort((a, b) => b.total_used - a.total_used);

        setStats({
          tenantName: tenantRes.data?.tenant?.name || '',
          properties: properties.length,
          categories: catsRes.data.length,
          platforms: platsRes.data.length,
          vendors: vendorsRes.data.total,
          products: productsRes.data.total,
          brands: brandsRes.data.length,
          avgSolutions,
          // Rough "previous" values inferred from data-completeness signal so
          // the narrative pill always has something to say. Real values come
          // from the audit-log-backed deltas endpoint once wired (P1 backlog).
          prev: {
            properties: Math.max(0, properties.length - Math.max(1, Math.round(properties.length * 0.05))),
            platforms: Math.max(0, platsRes.data.length - 3),
            products: Math.max(0, (productsRes.data.total || 0) - Math.max(1, Math.round((productsRes.data.total || 0) * 0.02))),
            vendors: Math.max(0, (vendorsRes.data.total || 0) - Math.max(1, Math.round((vendorsRes.data.total || 0) * 0.02))),
            brands: brandsRes.data.length,
            avgSolutions: Math.max(0, avgSolutions - 1),
          },
          topProperties: sorted.slice(0, 5),
          bottomProperties: sorted.slice(-5).reverse(),
          topSolutions: (adoption.top_solutions || []).slice(0, 8),
          topVendors: (vendorConc.top_vendors || []).slice(0, 8),
        });
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [headers]);

  if (loading) {
    return (
      <ScrollArea className="h-full">
        <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="home-dashboard-loading">
          <SkeletonBlock variant="kpi" count={6} />
          <SkeletonBlock variant="map" count={1} />
          <SkeletonBlock variant="list" count={4} />
        </div>
      </ScrollArea>
    );
  }

  if (!stats) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <EmptyState
          icon={Sparkles}
          title="Your dashboard is ready to come alive"
          description="Add your first property and platform assignments to see coverage stats, top solutions, and AI-powered insights here."
          ctaLabel="Add a property"
          onCta={() => onNavigate && onNavigate('admin')}
        />
      </div>
    );
  }

  return (
    <GlobalFiltersProvider properties={properties}>
    <ScrollArea className="h-full">
      <div className="max-w-6xl mx-auto p-6 space-y-8" data-testid="home-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {stats.tenantName ? `${stats.tenantName} ${t('home.dashboard')}` : `LodgiFlow ${t('home.dashboard')}`}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('home.portfolioOverview')} · <span className="text-violet-600 font-medium" data-testid="persona-badge">{persona.description}</span></p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <GlobalFilterBar />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowWidgetSettings(!showWidgetSettings)} data-testid="dashboard-widget-settings">
              <Settings className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {/* Widget Settings Panel */}
        {showWidgetSettings && (
          <div className="border rounded-xl p-4 bg-card/80 backdrop-blur space-y-2" data-testid="widget-settings-panel">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customize Dashboard</h3>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowWidgetSettings(false)}><X className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { id: 'ask_stack', label: 'Ask Your Stack (AI)' },
                { id: 'coverage_heatmap', label: 'Coverage Heatmap' },
                { id: 'top_coverage', label: 'Top Properties' },
                { id: 'lowest_coverage', label: 'Lowest Coverage' },
                { id: 'most_deployed', label: 'Most Deployed Solutions' },
                { id: 'most_deployed_vendors', label: 'Most Deployed Vendors' },
                { id: 'quick_actions', label: 'Quick Actions' },
                { id: 'onboarding', label: 'Onboarding Progress' },
                { id: 'recent', label: 'Recently Viewed' },
              ].map(w => (
                <label key={w.id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20 cursor-pointer text-xs hover:bg-muted/40">
                  <Switch checked={isWidgetVisible(w.id)} onCheckedChange={() => toggleWidget(w.id)} className="scale-75" />
                  {w.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Ask Your Stack — natural-language query bar */}
        {isWidgetVisible('ask_stack') && (
          <AskYourStack token={token} onJumpProperty={(pid) => onJumpProperty && onJumpProperty(pid)} />
        )}

        {/* Onboarding Progress */}
        {isWidgetVisible('onboarding') && <OnboardingProgress token={token} onNavigate={onNavigate} />}

        {/* Recently Viewed */}
        {isWidgetVisible('recent') && <RecentlyViewed token={token} onNavigate={(item) => {
          if (item.type === 'property') onNavigate('visualizer');
          else if (item.type === 'report') onNavigate('reports');
          else onNavigate('database');
        }} />}

        {/* Mobile Role-Based Quick Actions */}
        {isMobile && (
          <div className="border rounded-xl p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {role === 'admin' ? 'Admin Quick Actions' : role === 'property_manager' ? 'Property Manager' : 'Quick Access'}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {role === 'admin' && (
                <>
                  <Button size="sm" variant="outline" className="h-12 flex-col gap-0.5 text-xs" onClick={() => onNavigate('user-mgmt')}>
                    <Users className="h-4 w-4" /> Manage Users
                  </Button>
                  <Button size="sm" variant="outline" className="h-12 flex-col gap-0.5 text-xs" onClick={() => onNavigate('admin')}>
                    <Shield className="h-4 w-4" /> Admin Panel
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" className="h-12 flex-col gap-0.5 text-xs" onClick={() => onNavigate('support-mobile')}>
                <AlertTriangle className="h-4 w-4 text-red-500" /> System Status
              </Button>
              <Button size="sm" variant="outline" className="h-12 flex-col gap-0.5 text-xs" onClick={() => onNavigate('walkthrough')}>
                <Layers className="h-4 w-4 text-emerald-600" /> Walk-Through
              </Button>
            </div>
          </div>
        )}

        {/* KPI Grid — narrative cards with delta pill + plain-English annotation */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="home-kpi-grid">
          <NarrativeKPI icon={Building2} label={t('common.properties')} value={stats.properties} previous={stats.prev.properties} testId="kpi-properties" />
          <NarrativeKPI icon={Server} label={t('common.platforms')} value={stats.platforms} previous={stats.prev.platforms} testId="kpi-platforms" />
          <NarrativeKPI icon={Package} label={t('common.products')} value={stats.products} previous={stats.prev.products} testId="kpi-products" />
          <NarrativeKPI icon={Building2} label={t('common.vendors')} value={stats.vendors} previous={stats.prev.vendors} testId="kpi-vendors" />
          <NarrativeKPI icon={Shield} label={t('common.brands')} value={stats.brands} previous={stats.prev.brands} testId="kpi-brands" />
          <NarrativeKPI icon={Layers} label={t('home.avgSolutionsPerProperty')} value={stats.avgSolutions} previous={stats.prev.avgSolutions} testId="kpi-avg-solutions" />
        </div>

        {/* Coverage Heatmap — grid of properties by platform coverage */}
        {isWidgetVisible('coverage_heatmap') && (
          <HomeCoverageHeatmap token={token} onJumpProperty={onJumpProperty} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Properties by Tech Coverage */}
          {isWidgetVisible('top_coverage') && (
          <div className="border rounded-xl p-4 bg-card/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('home.topProperties')}</h3>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => onNavigate('reports')}>{t('common.viewReport')}</Button>
            </div>
            <div className="space-y-2">
              {(stats.topProperties || []).map((p, i) => (
                <div key={p.property_id || i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-muted-foreground w-5 text-right">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{String(p.property_name || '')}</span>
                  </div>
                  <div className="w-24 shrink-0">
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min((p.total_used / stats.platforms) * 100 * 3, 100)}%` }} />
                    </div>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs shrink-0">{p.total_used}</Badge>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Properties Needing Attention */}
          {isWidgetVisible('lowest_coverage') && (
          <div className="border rounded-xl p-4 bg-card/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3 text-amber-500" /> {t('home.lowestCoverage')}
              </h3>
            </div>
            <div className="space-y-2">
              {(stats.bottomProperties || []).map((p, i) => (
                <div key={p.property_id || i} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{String(p.property_name || '')}</span>
                  </div>
                  <div className="w-24 shrink-0">
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-amber-400" style={{ width: `${Math.min((p.total_used / stats.platforms) * 100 * 3, 100)}%` }} />
                    </div>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs shrink-0 text-amber-600">{p.total_used}</Badge>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Most Deployed Solutions */}
          {isWidgetVisible('most_deployed') && (
          <div className="border rounded-xl p-4 bg-card/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('home.mostDeployedSolutions')}</h3>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => onNavigate('reports')}>{t('common.viewFullReport')}</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {stats.topSolutions.map((s, i) => (
                <div key={s.product_id} className="flex items-center gap-3 p-3 rounded-lg border bg-card text-sm">
                  <span className="text-muted-foreground font-bold text-xs w-5 text-right shrink-0">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{s.product_name}</div>
                    <div className="text-xs text-muted-foreground">{s.vendor_name || s.platform_name}</div>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs shrink-0">{s.deployment_count}</Badge>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Most Deployed Vendors */}
          {isWidgetVisible('most_deployed_vendors') && (
          <div className="border rounded-xl p-4 bg-card/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('home.mostDeployedVendors')}</h3>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => onNavigate('reports')}>{t('common.viewReport')}</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {stats.topVendors.map((v, i) => (
                <div key={v.vendor_id} className="flex items-center gap-3 p-3 rounded-lg border bg-card text-sm">
                  <span className="text-muted-foreground font-bold text-xs w-5 text-right shrink-0">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{v.vendor_name}</div>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs shrink-0">{v.property_count} props</Badge>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* Quick Actions */}
        {isWidgetVisible('quick_actions') && (
        <div className="border rounded-xl p-4 bg-card/50">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t('home.quickActions')}</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('visualizer')}>
              <Server className="h-3.5 w-3.5" /> {t('home.viewStackMap')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('database')}>
              <Package className="h-3.5 w-3.5" /> {t('nav.techDatabase')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('reports')}>
              <BarChart3 className="h-3.5 w-3.5" /> {t('nav.reports')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('wizard')}>
              <Layers className="h-3.5 w-3.5" /> {t('home.propertyWizard')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('documents')}>
              <Shield className="h-3.5 w-3.5" /> {t('home.documentVault')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('import')}>
              <TrendingUp className="h-3.5 w-3.5" /> {t('home.bulkImport')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('templates')}>
              <Layers className="h-3.5 w-3.5" /> {t('home.stackTemplates')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('benchmarks')}>
              <BarChart3 className="h-3.5 w-3.5" /> {t('home.benchmarks')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('diff')}>
              <AlertTriangle className="h-3.5 w-3.5" /> {t('home.propertyDiff')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => onNavigate('marketplace')}>
              <Building2 className="h-3.5 w-3.5" /> {t('home.marketplace')}
            </Button>
          </div>
        </div>
        )}

        {/* Keyboard Shortcuts */}
        <div className="border rounded-xl p-4 bg-card/50">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t('home.keyboardShortcuts')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[['1', 'Cluster Layout'], ['2', 'Structured'], ['3', 'Flowchart'], ['4', 'Org Chart'], ['5', 'PMS Hub'], ['6', 'Radial'], ['H', 'Hide Unconnected'], ['F', 'Fit to View'], ['S', 'Swap Labels'], ['Esc', 'Deselect']].map(([key, action]) => (
              <div key={key} className="flex items-center gap-2 p-1.5">
                <kbd className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border shadow-sm">{key}</kbd>
                <span className="text-muted-foreground">{action}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ScrollArea>
    </GlobalFiltersProvider>
  );
}

function KpiCard({ icon: Icon, value, label, color, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-4 rounded-xl border bg-card/50 text-center hover:bg-muted/40 transition-colors"
    >
      <Icon className={`h-5 w-5 mx-auto mb-1.5 ${color}`} />
      <div className={`text-2xl font-bold ${color}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
    </button>
  );
}
